import { Col, Row, Tabs, List, Skeleton, Tag } from "antd";
import { DownCircleTwoTone } from "@ant-design/icons";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import Container from "shared/components/Container";
import Form from "shared/components/Form";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import { bindInputProps } from "utils/helpers";
import graphql from "utils/api/graphql";
import { useQuery } from "@apollo/client";
import Loading from "shared/components/Loading";
import Error from "shared/components/Error";
import { get } from "lodash";
import { useState } from "react";
import validation from "./validation";
import { useNavigate } from "react-router-dom";
import { SCREENS } from "utils/constants";

const tabs = {
  keys: {
    TAB_1: "GeneralInfo",
    TAB_2: "FiatAccounts",
    TAB_3: "CryptoAccounts",
    TAB_4: "Rules",
  },
  GeneralInfo: ({ formProps }) => {
    const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.ORGANIZATION);
    const FORM = t("FORM", { returnObjects: true });
    return (
      <Row gutter={[32]}>
        <Col
          xs={{ span: 24, order: 1 }}
          sm={{ span: 24, order: 1 }}
          md={{ span: 20, offset: 2, order: 1 }}
          lg={{ span: 14, offset: 5, order: 2 }}
        >
          <div className="inputs--container">
            <Form type={"vertical"}>
              <div className="field--item">
                <label>Name</label>
                <Input
                  placeholder={FORM.NAME}
                  {...bindInputProps({ name: "name", ...formProps })}
                />
              </div>
            </Form>
          </div>
        </Col>
      </Row>
    );
  },
  CryptoAccounts: () => {
    const { data, loading, error } = useQuery(graphql.queries.ADDRESSES, {
      variables: {
        where: { token: { chain: "crypto" } },
      },
    });

    if (loading) return <Loading />;
    if (error) return <Error />;

    const {
      addresses: { data: addresses },
    } = data;

    return (
      <List
        dataSource={addresses}
        renderItem={(address) => (
          <List.Item key={address.amount} actions={[]}>
            <Skeleton avatar title={false} loading={loading} active>
              <List.Item.Meta
                avatar={<DownCircleTwoTone />}
                title={address.value}
                description={
                  <div>
                    <Tag>{address.token.type}</Tag>
                    <Tag>
                      {Number(
                        get(address.token, "balances[0].value", 0)
                      ).toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 2,
                      })}
                    </Tag>
                  </div>
                }
              />
            </Skeleton>
          </List.Item>
        )}
      />
    );
  },
  FiatAccounts: () => {
    const { data, loading, error } = useQuery(graphql.queries.ADDRESSES, {
      variables: {
        where: { token: { chain: "fiat" } },
      },
    });

    if (loading) return <Loading />;
    if (error) return <Error />;

    const {
      addresses: { data: addresses },
    } = data;

    return (
      <List
        dataSource={addresses}
        renderItem={(address) => (
          <List.Item key={address.amount} actions={[]}>
            <Skeleton avatar title={false} loading={loading} active>
              <List.Item.Meta
                avatar={<DownCircleTwoTone />}
                title={address.value}
                description={
                  <div>
                    <Tag>{address.token.type}</Tag>
                    <Tag>
                      {Number(
                        get(address.token, "balances[0].value", 0)
                      ).toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 2,
                      })}
                    </Tag>
                  </div>
                }
              />
            </Skeleton>
          </List.Item>
        )}
      />
    );
  },
  Rules: () => {
    const { data, loading, error } = useQuery(graphql.queries.RULES, {
      variables: {},
    });

    if (loading) return <Loading />;
    if (error) return <Error />;

    const {
      rules: { data: rules },
    } = data;

    return (
      <List
        dataSource={rules}
        renderItem={(rule) => (
          <List.Item key={rule.id} actions={[]}>
            <Skeleton avatar title={false} loading={loading} active>
              <List.Item.Meta
                avatar={<DownCircleTwoTone />}
                title={rule.name}
                description={
                  <div>
                    <Tag>{rule.configuration.move}</Tag>
                    <Tag>{`${rule.configuration.ratio * 100}% ${
                      rule.configuration.tokens.fiat.id
                    } -> ${rule.configuration.tokens.crypto.id}`}</Tag>
                  </div>
                }
              />
            </Skeleton>
          </List.Item>
        )}
      />
    );
  },
};

const Detail = ({ onSubmit, organization, cancel }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.ORGANIZATION);
  const [tab, setTab] = useState(tabs.keys.TAB_4);
  const actions = (handleSubmit) => {
    return {
      [tabs.keys.TAB_1]: [
        {
          text: DETAIL.HEADER.SAVE,
          onClick: handleSubmit,
        },
        {
          text: DETAIL.HEADER.DISCARD,
          onClick: cancel,
          variant: "outlined",
        },
      ],
      [tabs.keys.TAB_2]: [
        {
          text: DETAIL.HEADER.NEW,
          onClick: () =>
            navigate(SCREENS.PRIVATE.ADMIN.ADDRESSES.CREATE.path, {
              state: { organization },
            }),
        },
        {
          text: DETAIL.HEADER.DISCARD,
          onClick: cancel,
          variant: "outlined",
        },
      ],
      [tabs.keys.TAB_3]: [
        {
          text: DETAIL.HEADER.DISCARD,
          onClick: cancel,
          variant: "outlined",
        },
      ],
      [tabs.keys.TAB_4]: [
        {
          text: DETAIL.HEADER.NEW,
          onClick: () =>
            navigate(SCREENS.PRIVATE.ADMIN.RULES.CREATE.path, {
              state: { organization },
            }),
        },
        {
          text: DETAIL.HEADER.DISCARD,
          onClick: cancel,
          variant: "outlined",
        },
      ],
    };
  };
  const DETAIL = t("DETAIL", { returnObjects: true });
  const FORM = t("FORM", { returnObjects: true });
  const { name } = organization;
  const Component = tabs[tab];

  return (
    <Container>
      <Formik
        initialValues={{ name }}
        onSubmit={onSubmit}
        validationSchema={validation(
          t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, {
            returnObjects: true,
          })
        )}
      >
        {({ isValid, handleSubmit, ...formProps }) => [
          <Tabs
            key="organization-detail-tab"
            tabBarExtraContent={
              <Header key={"header"} actions={actions(handleSubmit)[tab]} />
            }
            onTabClick={setTab}
            defaultActiveKey={tab}
            items={[
              {
                label: <span>{FORM.GENERAL_INFO}</span>,
                key: tabs.keys.TAB_1,
                children: <Component {...{ formProps }} />,
              },
              {
                label: <span>{FORM.FIAT_ACCOUNTS}</span>,
                key: tabs.keys.TAB_2,
                children: <Component {...{ formProps }} />,
              },
              {
                label: <span>{FORM.XO_ACCOUNTS}</span>,
                key: tabs.keys.TAB_3,
                children: <Component {...{ formProps }} />,
              },
              {
                label: <span>{FORM.RULES}</span>,
                key: tabs.keys.TAB_4,
                children: <Component {...{ formProps }} />,
              },
            ]}
          />,
        ]}
      </Formik>
    </Container>
  );
};

export default Detail;
